import './App.scss'
import Account from './Account'
import AppHeader from '../../components/AppHeader'
import AuthContext from '../../contexts/AuthContext'
import BannerManager, { clearBannerStates } from '../../components/BannerManager'
import Dashboard from './Dashboard'
// import Invoices from './Invoices'
import MeterRead from './MeterRead'
import PaymentOverview from './PaymentOverview'
import PrivacyPolicy from './PrivacyPolicy'
import React, { useContext, useState, useEffect } from 'react'
import SetupTwoFactorAuth from '../../components/TwoFactorAuth/SetupTwoFactorAuth'
import RequirePasswordChange from '../../components/RequirePasswordChange'
import SystemContractSigning from '../../components/SystemContract'
import SystemRegistration from './SystemRegistration'
import TermsAndConditions from './TermsAndConditions'
import WelcomeScreen from '../../components/WelcomeScreen'
import useAPI from '../../hooks/useAPI'
import { NavLink, Route, Switch } from 'react-router-dom'
import { loadIncompleteSystemContractDetails } from '../../services/loadIncompleteSystemContractDetails'

export default function App () {
  const {
    user,
    auth,
    sessionSettings,
    systems,
    system,
    userSawWelcomePage,
    incompleteSystemsAgreements,
    setIncompleteSystemsAgreements,
    onLogout,
    onChangeSystem
  } = useContext(AuthContext)

  const api = useAPI()
  let contractComponent = null;
  const [profileActionsOpen, setProfileActionsOpen] = useState(false)
  const [settings] = useState(sessionSettings)
  const [isStartButtonClicked, setIsStartButtonClicked] = useState(false)

  const handleLogout = () => {
    // Clear all banner dismissal states on logout
    clearBannerStates(system)
    onLogout()
  }

  const shouldShowMeterReadForm = () => {
    const access = system.resource.online_monitoring_access_type?.toLowerCase()
    return access === 'self' || (access === 'other' && system.resource.online_monitoring_self_report)
  }

  const getNavigationItems = () => {
    const nav = [
      { path: '/', label: 'System Registration', icon: 'leaderboard', component: SystemRegistration, exact: true },
      { path: '/dashboard', label: 'Dashboard', icon: 'dashboard', component: Dashboard, exact: true },
      { path: '/payment-history', label: 'Payments', icon: 'credit_card', component: PaymentOverview, exact: true },
      // { path: '/invoices', label: 'Invoices', icon: 'receipt_long', component: Invoices, exact: true },
      { path: '/account', label: 'My Account', icon: 'person', component: Account, exact: false },
    ]
    if (shouldShowMeterReadForm()) {
      nav.splice(2, 0, {
        path: '/meter-read-reporting',
        label: 'Meter Read Reporting',
        icon: 'receipt_long',
        component: MeterRead,
        exact: true
      })
    }
    return nav
  }

  useEffect(() => {
    if (auth.two_factor_auth_type !== null && system?.id && !contractComponent) {
      (async () => {
        const result = await loadIncompleteSystemContractDetails(api, system)

        if (result?.errors?.contract_status) {
          handleLogout();
          return
        }

        if (system.is_owner) {
          api.updateIncompleteSystemsAgreementsData(result.incomplete_systems_agreements)
          setIncompleteSystemsAgreements(result.incomplete_systems_agreements)
        }
      })()
    }
    // eslint-disable-next-line
  }, [system?.id, contractComponent])

  const firstPendingAgreement = () => {
    const systemAgreements = incompleteSystemsAgreements
      .filter((s) => !s.is_sent_contract_status && system.is_owner && s.state_can_see_contract_signing_flow)
      .find(s => s.id === system?.id)

    if (!systemAgreements) return null

    const pending = Object.entries(systemAgreements.incomplete_agreements || {})
      .filter(([key, ag]) =>
        !ag.is_completed &&
        (key !== 'security_addendum' || systemAgreements.incomplete_agreements['srec_agreement']?.has_upfront_payment)
      )
      .map(([key, ag]) => ({ agreementKey: key, agreement: ag, systemId: systemAgreements.id }))

    if (pending.length > 0) {
      return pending[0]
    }

    if (!user.payment_method_type) {
      return {
        agreementKey: 'payment_method_selector',
        agreement: {},
        systemId: systemAgreements.id
      }
    }

    return null
  }

  const renderSystemContractSigning = () => {
    const agreement = firstPendingAgreement()
    if (agreement) {
      return !userSawWelcomePage && !isStartButtonClicked
        ? <WelcomeScreen handleStartClick={handleStartClick}/>
        : <SystemContractSigning
          key={agreement.agreementKey}
          templateTypeKey={agreement.agreementKey}
          systemId={agreement.systemId}
          clickWrapId={agreement.agreement?.elastic_template_uuid}
          agreeURL={agreement.agreement?.agree_url}
        />
    }
    return null
  }

  const handleStartClick = () => {
    setIsStartButtonClicked(true)
    api.updateUserSawWelcomePage(true)
  }

  if (!auth.two_factor_auth_enabled && auth.two_factor_auth_type !== 'NONE') {
    return <SetupTwoFactorAuth/>
  }

  if (auth.require_password_change) {
    return <RequirePasswordChange email={user.email} token={auth.password_reset_token} />;
  }

  contractComponent = renderSystemContractSigning()
  if (contractComponent) return contractComponent

  const navItems = getNavigationItems()

  return (
    <div
      className="app-container"
      style={!auth.two_factor_auth_enabled && auth.two_factor_auth_type !== 'NONE' ? { filter: 'blur(2px)' } : {}}
      onClick={() => setProfileActionsOpen(false)}
    >
      {!settings.menu_closed && (
        <div className="app-navigation">
          <div className="app-navigation--header">
            <img src={require('../../images/csg-logo-blue.png')} alt="logo"/>
          </div>
          <nav className="app-main-nav-container">
            <ul className="app-main-nav">
              {navItems.map((item, i) => (
                <li key={i} className="app-main-nav--item--container">
                  <NavLink exact={item.exact} to={item.path} className="app-main-nav--item">
                    <span className="app-main-nav--item--icon">
                      <span className="material-symbols-outlined material-icons">{item.icon}</span>
                    </span>
                    <span className="app-main-nav--item--label">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
      <div className="app-content-container">
        <AppHeader
          user={user}
          systems={systems}
          system={system}
          onChangeSystem={onChangeSystem}
          onLogout={handleLogout}
          profileActionsOpen={profileActionsOpen}
          setProfileActionsOpen={setProfileActionsOpen}
        />
        <BannerManager system={system}/>
        <div className="app-content">
          <Switch>
            {navItems.map((item) => (
              <Route key={item.path} {...item} />
            ))}
            <Route component={TermsAndConditions} path="/terms-and-conditions"/>
            <Route component={PrivacyPolicy} path="/privacy-policy"/>
          </Switch>
          <footer>
            <ul>
              <li><NavLink to="/privacy-policy">Privacy Policy</NavLink></li>
              <li><NavLink to="/terms-and-conditions">Terms and Conditions</NavLink></li>
            </ul>
          </footer>
        </div>
      </div>
    </div>
  )
}
